import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Radio,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { CheckCircle, Print } from "@mui/icons-material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import ExpandableView from "../../components/common/ExpandableView";
import LoadingPanel from "../../components/common/LoadingPanel";
import ErrorMessage from "../../components/common/ErrorMessage";
import ModalBackground from "../../components/common/ModalBackground";
import Combobox from "../../components/common/Combobox";
import { BackForeColor } from "../../components/common/BackForeColor";
import SettlementDetailCard from "./components/SettlementDetailCard";
import SettlementFilters from "./components/SettlementFilters";
import {
  EMPTY_SETTLEMENT_FILTERS,
  getSettlementFilterOptions,
  filterSettlementDetails,
  buildSettlementFilterQuery,
} from "./components/settlementFilterUtils";
import localize from "../../common/localize";
import { getValueInCurrency } from "../../common/utils";
import { getInputLabelProps, getInputProps, PopperFullWidth } from "../../common/appStyles";
import {
  getCurrencyFromSetup,
  getSettlementStatusName,
  getSettlementStatusBackColor,
  getSettlementStatusForeColor,
  getUserName,
  SettlementStatusEnum,
} from "../../common/EntitiesManagement";

const getCode = (uuid) => (uuid ? uuid.split("-")[0] : localize.no_data);

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : localize.no_data;

export default function SettlementDetail() {
  const { config, SETTLEMENT_ENDPOINT, PERSONAL_API } = useGlobalContext();
  const { uuid } = useParams();

  const [settlement, setSettlement] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [expandAll, setExpandAll] = useState(true);
  const [filters, setFilters] = useState(EMPTY_SETTLEMENT_FILTERS);
  const [personalData, setPersonalData] = useState([]);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editDetailId, setEditDetailId] = useState("");
  const [editObservation, setEditObservation] = useState("");
  const [editValue, setEditValue] = useState(0);
  const [editPersonal, setEditPersonal] = useState(null);

  // Status change modal state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusDetailId, setStatusDetailId] = useState("");
  const [statusCurrent, setStatusCurrent] = useState(null);
  const [statusChoice, setStatusChoice] = useState(null);
  const [statusObservation, setStatusObservation] = useState("");

  // Settlement-level status change modal state
  const [showSettlementStatusModal, setShowSettlementStatusModal] =
    useState(false);
  const [settlementStatusChoice, setSettlementStatusChoice] = useState(null);
  const [settlementStatusObservation, setSettlementStatusObservation] =
    useState("");
  const [showStatusSuccess, setShowStatusSuccess] = useState(false);

  const OBSERVATION_MAX = 150;

  const normalizeList = (body) =>
    Array.isArray(body)
      ? body
      : (body?.Data ?? body?.responseData ?? []);

  const reloadDetail = async () => {
    // Silent refresh (no loading panel) so scroll/expand position is kept.
    const detResp = await axios.get(
      `${SETTLEMENT_ENDPOINT}/${uuid}/detail`,
      config,
    );
    setDetail(normalizeList(detResp?.data));
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const resp = await axios.get(`${SETTLEMENT_ENDPOINT}/${uuid}`, config);
        const data =
          resp?.data?.Data ?? resp?.data?.responseData ?? resp?.data ?? null;
        if (!active) return;
        setSettlement(data);

        setLoadingDetail(true);
        const detResp = await axios.get(
          `${SETTLEMENT_ENDPOINT}/${uuid}/detail`,
          config,
        );
        if (!active) return;
        setDetail(normalizeList(detResp?.data));
      } catch (error) {
        if (!active) return;
        setErrorMessage(error?.message ?? localize.error_unexpected);
        setShowErrorModal(true);
      } finally {
        if (active) setLoadingDetail(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [uuid]);

  useEffect(() => {
    const getPersonal = async () => {
      try {
        const requestConfig = {
          ...config,
          headers: { ...config.headers, pagination: -1 },
        };
        const resp = await axios.get(`${PERSONAL_API}/`, requestConfig);
        setPersonalData(normalizeList(resp?.data));
      } catch {
        setPersonalData([]);
      }
    };
    getPersonal();
  }, []);

  const openEditModal = (item) => {
    const detailId = item?.id ?? item?.Uuid ?? "";
    setEditDetailId(detailId);
    setEditObservation("");
    setEditValue(item?.value ?? 0);
    const assigned = item?.personal
      ? (personalData.find((p) => p?.Id === item.personal.id) ?? {
          ...item.personal,
          Id: item.personal.id,
        })
      : null;
    setEditPersonal(assigned);
    setShowEditModal(true);
  };

  const submitEdit = async () => {
    try {
      const body = {
        Uuid: editDetailId,
        Observations: editObservation ? editObservation : null,
        PersonalId: editPersonal?.Id ?? editPersonal?.id ?? 0,
        Value: Number(editValue) || 0,
        CreatedBy: getUserName(),
      };
      await axios.put(
        `${SETTLEMENT_ENDPOINT}/${uuid}/detail/${editDetailId}`,
        body,
        config,
      );
      setShowEditModal(false);
      await reloadDetail();
    } catch (error) {
      setErrorMessage(error?.message ?? localize.error_unexpected);
      setShowErrorModal(true);
    }
  };

  const renderEditForm = () => (
    <Box className="flex flex-col gap-3 mt-2">
      <Box>
        <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
          {localize.settlement_detail_identifier}
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
          {editDetailId ? editDetailId.split("-")[0] : localize.no_data}
        </Typography>
      </Box>
      <Combobox
        id="EditPersonal"
        fieldLabel={localize.settlement_assigned_personal}
        fieldOptions={personalData}
        fieldValue={editPersonal}
        handleValueChange={(value) => setEditPersonal(value)}
        popperFullWidth={PopperFullWidth}
        customOptionLabel={(option) =>
          `${option.Identification} - ${option.SurName} ${option.LastName}`
        }
      />
      <TextField
        type="number"
        label={localize.settlement_settled_value}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        size="small"
        fullWidth
        InputLabelProps={getInputLabelProps()}
        inputProps={getInputProps()}
      />
      <Alert severity="warning" className="w-full">
        <span className="text-xs">
          {localize.settlement_observation_required}
        </span>
      </Alert>
      <TextField
        required
        label={localize.observations}
        value={editObservation}
        onChange={(e) =>
          setEditObservation(e.target.value.slice(0, OBSERVATION_MAX))
        }
        multiline
        minRows={3}
        maxRows={6}
        fullWidth
        InputLabelProps={getInputLabelProps()}
        inputProps={{ maxLength: OBSERVATION_MAX }}
        helperText={`${editObservation.length}/${OBSERVATION_MAX}`}
      />
    </Box>
  );

  const openStatusModal = (item) => {
    setStatusDetailId(item?.id ?? item?.Uuid ?? "");
    setStatusCurrent(item?.Status);
    setStatusChoice(null);
    setStatusObservation("");
    setShowStatusModal(true);
  };

  const isDetailPaymentMode = () =>
    statusCurrent === SettlementStatusEnum.Approved;

  const submitStatusChange = async () => {
    try {
      const paymentMode = isDetailPaymentMode();
      const newStatus = paymentMode
        ? SettlementStatusEnum.Payed
        : statusChoice?.Id;
      const body = {
        status: newStatus,
        createdBy: getUserName(),
        observations:
          !paymentMode && newStatus === 2 && statusObservation
            ? statusObservation
            : null,
      };
      const resp = await axios.put(
        `${SETTLEMENT_ENDPOINT}/${uuid}/detail/${statusDetailId}/status`,
        body,
        config,
      );
      if (resp?.data?.responseCode === 1) {
        // Logical failure: close the drawer first so the error modal is not
        // rendered behind it, then surface the server message.
        setShowStatusModal(false);
        setErrorMessage(
          resp?.data?.responseMessage ?? localize.error_unexpected,
        );
        setShowErrorModal(true);
        return;
      }
      setShowStatusModal(false);
      const changedId = statusDetailId;
      await reloadDetail();
      // Keep the just-changed settlement detail visible on screen.
      setTimeout(() => {
        const el = document.getElementById(`sd-${changedId}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 150);
    } catch (error) {
      setShowStatusModal(false);
      setErrorMessage(error?.message ?? localize.error_unexpected);
      setShowErrorModal(true);
    }
  };

  const renderApproveRejectCards = (choice, onChoose) => (
    <Box sx={{ display: "flex", gap: 2 }}>
      <Card
        variant="outlined"
        onClick={() => onChoose({ Id: 1, Name: localize.approved })}
        sx={{
          flex: 1,
          border: choice?.Id === 1 ? "2px solid #2E7D32" : "1px solid #E0E0E0",
          backgroundColor: choice?.Id === 1 ? "#E8F5E9" : "transparent",
          cursor: "pointer",
          borderRadius: "8px",
        }}
      >
        <CardActionArea
          sx={{
            p: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Radio
            size="small"
            checked={choice?.Id === 1}
            sx={{ color: "#2E7D32", "&.Mui-checked": { color: "#2E7D32" } }}
          />
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#2E7D32" }}>
            {localize.approved}
          </Typography>
        </CardActionArea>
      </Card>

      <Card
        variant="outlined"
        onClick={() => onChoose({ Id: 2, Name: localize.rejected })}
        sx={{
          flex: 1,
          border: choice?.Id === 2 ? "2px solid #C62828" : "1px solid #E0E0E0",
          backgroundColor: choice?.Id === 2 ? "#FFEBEE" : "transparent",
          cursor: "pointer",
          borderRadius: "8px",
        }}
      >
        <CardActionArea
          sx={{
            p: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Radio
            size="small"
            checked={choice?.Id === 2}
            sx={{ color: "#C62828", "&.Mui-checked": { color: "#C62828" } }}
          />
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#C62828" }}>
            {localize.rejected}
          </Typography>
        </CardActionArea>
      </Card>
    </Box>
  );

  const renderStatusForm = () => {
    if (isDetailPaymentMode()) {
      return (
        <Box className="flex flex-col gap-3 mt-2" sx={{ mb: "10px" }}>
          <Box>
            <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
              {localize.settlement_detail_identifier}
            </Typography>
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
              {statusDetailId
                ? statusDetailId.split("-")[0]
                : localize.no_data}
            </Typography>
          </Box>
          <Typography
            className="text-center"
            sx={{ fontSize: "0.95rem", fontWeight: 600 }}
          >
            {localize.settlement_payment_confirm_question}
          </Typography>
          <Alert severity="info" className="w-full">
            <span className="text-xs">
              {localize.settlement_detail_payment_confirm_info}
            </span>
          </Alert>
        </Box>
      );
    }
    return (
      <Box className="flex flex-col gap-3 mt-2">
      <Box>
        <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
          {localize.settlement_detail_identifier}
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
          {statusDetailId ? statusDetailId.split("-")[0] : localize.no_data}
        </Typography>
      </Box>
      <Box>
        <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
          {localize.current_status}
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          <BackForeColor
            styleName="text-xs my-0"
            backColor={getSettlementStatusBackColor(statusCurrent)}
            foreColor={getSettlementStatusForeColor(statusCurrent)}
            text={getSettlementStatusName(statusCurrent)}
          />
        </Box>
      </Box>
      <Box>
        <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF", mb: 0.5 }}>
          {localize.new_status}
        </Typography>
        {renderApproveRejectCards(statusChoice, setStatusChoice)}
      </Box>
      {statusChoice?.Id === 2 && (
        <>
          <Alert severity="warning" className="w-full">
            <span className="text-xs">
              {localize.settlement_observation_required}
            </span>
          </Alert>
          <TextField
            required
            label={localize.observations}
            value={statusObservation}
            onChange={(e) =>
              setStatusObservation(e.target.value.slice(0, OBSERVATION_MAX))
            }
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            InputLabelProps={getInputLabelProps()}
            inputProps={{ maxLength: OBSERVATION_MAX }}
            helperText={`${statusObservation.length}/${OBSERVATION_MAX}`}
          />
        </>
      )}
    </Box>
    );
  };

  const isSettlementPaymentMode = () => {
    const settlementStatus =
      settlement?.status ?? settlement?.SettlementStatus ?? settlement?.Status;
    const hasPendingDetail = (detail ?? []).some((d) => d?.Status === 0);
    return (
      (settlementStatus === SettlementStatusEnum.Approved ||
        settlementStatus === SettlementStatusEnum.Partial) &&
      !hasPendingDetail
    );
  };

  const openSettlementStatusModal = () => {
    setSettlementStatusChoice(null);
    setSettlementStatusObservation("");
    setShowSettlementStatusModal(true);
  };

  const submitSettlementStatusChange = async () => {
    try {
      const paymentMode = isSettlementPaymentMode();
      const newStatus = paymentMode ? 3 : settlementStatusChoice?.Id;
      const body = {
        status: newStatus,
        createdBy: getUserName(),
        observations:
          !paymentMode &&
          settlementStatusChoice?.Id === 2 &&
          settlementStatusObservation
            ? settlementStatusObservation
            : null,
      };
      const resp = await axios.put(
        `${SETTLEMENT_ENDPOINT}/${uuid}/status`,
        body,
        config,
      );
      if (resp?.data?.responseCode === 0) {
        setShowSettlementStatusModal(false);
        setShowStatusSuccess(true);
        return;
      }
      setShowSettlementStatusModal(false);
      setErrorMessage(
        resp?.data?.responseMessage ?? localize.error_unexpected,
      );
      setShowErrorModal(true);
    } catch (error) {
      setShowSettlementStatusModal(false);
      setErrorMessage(error?.message ?? localize.error_unexpected);
      setShowErrorModal(true);
    }
  };

  const renderSettlementStatusForm = () => {
    if (isSettlementPaymentMode()) {
      return (
        <Box className="flex flex-col gap-3 mt-2" sx={{ mb: "10px" }}>
          <Typography
            className="text-center"
            sx={{ fontSize: "0.95rem", fontWeight: 600 }}
          >
            {localize.settlement_payment_confirm_question}
          </Typography>
          <Alert severity="info" className="w-full">
            <span className="text-xs">
              {localize.settlement_payment_confirm_info}
            </span>
          </Alert>
        </Box>
      );
    }
    return (
      <Box className="flex flex-col gap-3 mt-2" sx={{ mb: "10px" }}>
        <Alert severity="info" className="w-full">
          <span className="text-xs">
            {localize.settlement_status_change_message}
          </span>
        </Alert>
        <Box>
          <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF", mb: 0.5 }}>
            {localize.new_status}
          </Typography>
          {renderApproveRejectCards(
            settlementStatusChoice,
            setSettlementStatusChoice,
          )}
        </Box>
        {settlementStatusChoice?.Id === 2 && (
          <>
            <Alert severity="warning" className="w-full">
            <span className="text-xs">
              {localize.settlement_observation_required}
            </span>
          </Alert>
          <TextField
            required
            label={localize.observations}
            value={settlementStatusObservation}
            onChange={(e) =>
              setSettlementStatusObservation(
                e.target.value.slice(0, OBSERVATION_MAX),
              )
            }
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            InputLabelProps={getInputLabelProps()}
            inputProps={{ maxLength: OBSERVATION_MAX }}
            helperText={`${settlementStatusObservation.length}/${OBSERVATION_MAX}`}
          />
        </>
      )}
    </Box>
    );
  };

  if (!settlement) {
    return <LoadingPanel />;
  }

  const code = getCode(settlement?.Uuid ?? uuid);
  const status = settlement?.status ?? settlement?.SettlementStatus ?? settlement?.Status;
  const totalValue = settlement?.totalValue ?? settlement?.TotalValue ?? 0;
  const approvedTotal = (detail ?? [])
    .filter((d) => d?.Status === 1 || d?.Status === 3)
    .reduce((sum, d) => sum + (d?.value ?? 0), 0);
  const subtitle = `${formatDate(settlement?.ModificationDate)} · ${localize.created_by}: ${settlement?.CreatedBy ?? localize.no_data}`;

  const renderDetailCard = (item) => (
    <SettlementDetailCard
      item={item}
      onStatusClick={openStatusModal}
      onEditClick={openEditModal}
    />
  );

  const filterOptions = getSettlementFilterOptions(detail);
  const filteredDetail = filterSettlementDetails(detail, filters);

  const buildEventGroups = (list) => {
    const map = new Map();
    (list ?? []).forEach((item) => {
      const key = item?.event?.Id ?? "unknown";
      if (!map.has(key)) {
        map.set(key, {
          code: item?.event?.Code,
          items: [],
        });
      }
      map.get(key).items.push(item);
    });
    return Array.from(map.values()).map((group) => {
      const total = group.items.reduce(
        (sum, item) => sum + (item?.value ?? 0),
        0,
      );
      const code = group.code ?? localize.no_data;
      const totalText = getValueInCurrency(getCurrencyFromSetup(), total);
      return {
        Name: `${code} (${totalText})`,
        Component: (
          <Box className="flex flex-col gap-3 w-full">
            {group.items.map((item) => renderDetailCard(item))}
          </Box>
        ),
      };
    });
  };

  return (
    <div
      className="lateral_padding"
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "calc(100vh - 90px)",
      }}
    >
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          title={localize.settlement_edit_detail_title}
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          okButtonText={localize.save_changes}
          okButtonAlwaysAvailable
          validators={!editObservation.trim()}
          onClickToSubmit={submitEdit}
          onClickToCancel={() => setShowEditModal(false)}
          Component={renderEditForm()}
        />
      )}

      {showStatusModal && (
        <ModalBackground
          isDrawerMode={!isDetailPaymentMode()}
          title={localize.settlement_change_status_title}
          showModal={showStatusModal}
          setShowModal={setShowStatusModal}
          okButtonText={localize.save_changes}
          okButtonAlwaysAvailable
          validators={
            isDetailPaymentMode()
              ? false
              : !statusChoice ||
                (statusChoice?.Id === 2 && !statusObservation.trim())
          }
          onClickToSubmit={submitStatusChange}
          onClickToCancel={() => setShowStatusModal(false)}
          Component={renderStatusForm()}
        />
      )}

      {showSettlementStatusModal && (
        <ModalBackground
          title={localize.settlement_change_status_title}
          showModal={showSettlementStatusModal}
          setShowModal={setShowSettlementStatusModal}
          okButtonText={localize.save_changes}
          okButtonAlwaysAvailable
          validators={
            isSettlementPaymentMode()
              ? false
              : !settlementStatusChoice ||
                (settlementStatusChoice?.Id === 2 &&
                  !settlementStatusObservation.trim())
          }
          onClickToSubmit={submitSettlementStatusChange}
          onClickToCancel={() => setShowSettlementStatusModal(false)}
          Component={renderSettlementStatusForm()}
        />
      )}

      {showStatusSuccess && (
        <ModalBackground
          title={localize.settlement_change_status_title}
          showModal={showStatusSuccess}
          setShowModal={setShowStatusSuccess}
          hideCancelButton
          okButtonText={localize.accept}
          okButtonAlwaysAvailable
          onClickToSubmit={() => window.location.reload()}
          Component={
            <Box className="flex flex-col items-center gap-2 mt-2">
              <CheckCircle sx={{ color: "#2E7D32", fontSize: 44 }} />
              <Typography className="text-center" sx={{ fontSize: "0.9rem" }}>
                {localize.settlement_status_change_success}
              </Typography>
            </Box>
          }
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent className="flex flex-col gap-3">
          <Box className="flex flex-row flex-wrap items-center justify-between gap-3">
            <Box className="flex flex-row items-center gap-2">
              <Tooltip title={localize.print} arrow>
                <IconButton
                  aria-label="print"
                  onClick={() => {
                    const query = buildSettlementFilterQuery(filters);
                    const win = window.open(
                      `/settlement/${uuid}/report${query ? `?${query}` : ""}`,
                      "_blank",
                    );
                    if (win) win.focus();
                  }}
                  sx={{ color: "#10366E" }}
                >
                  <Print />
                </IconButton>
              </Tooltip>
              <PageTitleSubtitle
                title={`${localize.settlement_detail_title} - ${code}`}
                subtitle={subtitle}
                useEventTitleStyle
              />
            </Box>
            <Box className="flex flex-row flex-wrap gap-2">
              <Card variant="outlined" sx={{ minWidth: 160 }}>
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
                    {localize.current_status}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.5,
                      cursor:
                        status === 0 || status === 1 || status === 4
                          ? "pointer"
                          : "default",
                    }}
                    onClick={
                      status === 0 || status === 1 || status === 4
                        ? openSettlementStatusModal
                        : undefined
                    }
                  >
                    <BackForeColor
                      styleName="text-xs"
                      backColor={getSettlementStatusBackColor(status)}
                      foreColor={getSettlementStatusForeColor(status)}
                      text={getSettlementStatusName(status)}
                      TooltipText={
                        status === 0 || status === 1 || status === 4
                          ? localize.settlement_change_status_title
                          : undefined
                      }
                    />
                  </Box>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ minWidth: 160 }}>
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
                    {localize.settlement_settled_value}
                  </Typography>
                  <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: "#10366E", mt: 0.5 }}>
                    {getValueInCurrency(getCurrencyFromSetup(), totalValue)}
                  </Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ minWidth: 160 }}>
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
                    {localize.settlement_total_approved}
                  </Typography>
                  <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: "#2E7D32", mt: 0.5 }}>
                    {getValueInCurrency(getCurrencyFromSetup(), approvedTotal)}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {settlement?.Observations && (
            <Alert severity="info" className="w-full">
              <span className="text-xs">
                <b>{`${localize.observations}: `}</b>
                {settlement.Observations}
              </span>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Box
        sx={{
          mx: "16px",
          mb: "16px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Divider />
        {loadingDetail && !detail ? (
          <Box className="flex flex-row items-center gap-2 mt-3">
            <CircularProgress size={20} />
            <Typography sx={{ fontSize: "0.85rem", color: "#6B7280" }}>
              {localize.loading}
            </Typography>
          </Box>
        ) : (
          <Box
            className="mt-3"
            sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 1 }}
          >
            {detail?.length > 0 ? (
              (() => {
                const groups = buildEventGroups(filteredDetail);
                return (
                  <>
                    <SettlementFilters
                      options={filterOptions}
                      filters={filters}
                      setFilters={setFilters}
                    />
                    <Box className="flex justify-between items-center mb-2 gap-2">
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#374151" }}>
                        {`${localize.settlement_total_settled_orders}: ${groups.length}`}
                      </Typography>
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        sx={{ fontSize: "0.8rem", color: "#1976d2" }}
                        onClick={() => setExpandAll((prev) => !prev)}
                      >
                        {expandAll
                          ? localize.settlement_collapse_all
                          : localize.settlement_expand_all}
                      </Link>
                    </Box>
                    {groups.length > 0 ? (
                      <ExpandableView
                        key={expandAll ? "expanded" : "collapsed"}
                        components={groups}
                        defaultExpandedIndexes={
                          expandAll ? groups.map((_, index) => index) : []
                        }
                      />
                    ) : (
                      <Typography sx={{ fontSize: "0.85rem", color: "#6B7280" }}>
                        {localize.no_data}
                      </Typography>
                    )}
                  </>
                );
              })()
            ) : (
              <Typography sx={{ fontSize: "0.85rem", color: "#6B7280" }}>
                {localize.no_data}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </div>
  );
}
